// src/automation/components/MappingEditor.js
//
// Per-CSV mapping manager for a task. For each file in the task's `maps`:
//   • Download — pull the current CSV from S3 and save it locally
//   • Replace  — upload a new CSV; its headers must match the existing file's
//                columns (set, order-insensitive) or it's rejected, then saved
//
// (Full in-grid cell editing was intentionally dropped — editing CSVs is better
// done offline: download, edit in a spreadsheet, replace.)
//
// Data flows through the dispatcher (get_map / save_map). A small local CSV
// parser is used only to read the uploaded file's header row for validation.

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, Upload, Eye, EyeOff, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { getMap, saveMap, getMapFileInfo, downloadMapFile, uploadMapFile } from "../api/automation_data";

// Parse just enough CSV to read the header row (handles simple quoting).
function parseHeaderAndRows(text) {
  const rows = [];
  let row = [];
  let field = "";
  let i = 0;
  let inQ = false;
  const pushF = () => {
    row.push(field);
    field = "";
  };
  const pushR = () => {
    rows.push(row);
    row = [];
  };
  while (i < text.length) {
    const c = text[i];
    if (inQ) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQ = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQ = true;
      i++;
      continue;
    }
    if (c === ",") {
      pushF();
      i++;
      continue;
    }
    if (c === "\r") {
      i++;
      continue;
    }
    if (c === "\n") {
      pushF();
      pushR();
      i++;
      continue;
    }
    field += c;
    i++;
  }
  if (field.length > 0 || row.length > 0) {
    pushF();
    pushR();
  }
  if (rows.length === 0) return { headers: [], rows: [] };
  const headers = rows[0].map((h) => h.trim());
  const body = rows.slice(1).filter((r) => r.some((c) => c !== ""));
  return { headers, rows: body };
}

function sameHeaderSet(a, b) {
  const na = new Set(a.map((x) => String(x).trim().toLowerCase()));
  const nb = new Set(b.map((x) => String(x).trim().toLowerCase()));
  if (na.size !== nb.size) return false;
  for (const x of na) if (!nb.has(x)) return false;
  return true;
}

function toCsv(headers, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(esc).join(",")];
  for (const r of rows) lines.push(headers.map((_, i) => esc(r[i])).join(","));
  return lines.join("\n");
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function FileRow({ taskName, file, canEdit = false }) {
  const qc = useQueryClient();
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(false);

  const { data, refetch, isFetching } = useQuery({
    queryKey: ["map", taskName, file],
    queryFn: () => getMap(taskName, file),
    enabled: false,
  });

  const saveMutation = useMutation({
    mutationFn: (payload) => saveMap(taskName, file, payload),
    onSuccess: (res) => {
      setNotice({ type: "ok", msg: res?.summary || "Replaced." });
      qc.invalidateQueries({ queryKey: ["map", taskName, file] });
    },
    onError: (e) => setNotice({ type: "err", msg: e?.message || "Replace failed" }),
  });

  const ensureLoaded = async () => data || (await refetch()).data;

  const handleView = async () => {
    setNotice(null);
    if (viewing) {
      setViewing(false);
      return;
    }
    const res = await ensureLoaded();
    if (!res) {
      setNotice({ type: "err", msg: "Could not load file." });
      return;
    }
    setViewing(true);
  };

  const handleDownload = async () => {
    setNotice(null);
    setBusy(true);
    try {
      const res = await ensureLoaded();
      if (!res) {
        setNotice({ type: "err", msg: "Could not load file." });
        return;
      }
      download(file, res.csv || toCsv(res.headers, res.rows));
    } finally {
      setBusy(false);
    }
  };

  const handleReplace = async (fileObj) => {
    setNotice(null);
    setBusy(true);
    try {
      if (/\.xlsx$/i.test(fileObj.name)) {
        setNotice({
          type: "err",
          msg: `${fileObj.name} is an Excel workbook, but "${file}" is a CSV map. ` +
            "Workbook maps must be declared with a .xlsx name in the task's maps.",
        });
        return;
      }
      const res = await ensureLoaded();
      const text = await fileObj.text();
      const parsed = parseHeaderAndRows(text);
      if (!parsed.headers.length) {
        setNotice({ type: "err", msg: "The file has no header row." });
        return;
      }
      // No current file yet (first upload): nothing to compare the columns with.
      if (!res || !(res.headers || []).length) {
        if (!window.confirm(`No existing ${file} found. Upload this file as the first version?\n\n` +
          `Columns: ${parsed.headers.join(", ")}`)) return;
        saveMutation.mutate({ headers: parsed.headers, rows: parsed.rows });
        return;
      }
      if (!sameHeaderSet(parsed.headers, res.headers)) {
        setNotice({
          type: "err",
          msg: `Schema mismatch. Expected columns: ${res.headers.join(", ")}`,
        });
        return;
      }
      saveMutation.mutate({ headers: res.headers, rows: parsed.rows });
    } finally {
      setBusy(false);
    }
  };

  const working = busy || saveMutation.isPending;

  return (
    <div className="auto-map">
      <div className="auto-map-head">
        <span className="auto-map-name">{file}</span>
        <div className="auto-map-actions">
          <button className="auto-eb-btn" onClick={handleView} disabled={working}>
            {isFetching && !viewing ? (
              <RefreshCw size={14} className="auto-eb-spin" />
            ) : viewing ? (
              <EyeOff size={14} />
            ) : (
              <Eye size={14} />
            )}
            {viewing ? "Hide" : "View"}
          </button>
          <button className="auto-eb-btn" onClick={handleDownload} disabled={working}>
            {busy ? <RefreshCw size={14} className="auto-eb-spin" /> : <Download size={14} />} Download
          </button>
          {canEdit && (
            <label className={`auto-eb-btn auto-eb-btn--primary ${working ? "auto-eb-btn--disabled" : ""}`}>
              <Upload size={14} /> Replace
              <input
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                disabled={working}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleReplace(f);
                  e.target.value = "";
                }}
              />
            </label>
          )}
        </div>
      </div>
      {canEdit && (
        <p className="auto-map-hint">Replace must keep the same columns as the current file.</p>
      )}
      {notice && (
        <div className={`auto-eb-alert auto-eb-alert--${notice.type}`}>
          {notice.type === "ok" ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
          <span>{notice.msg}</span>
        </div>
      )}

      {viewing && data && (
        <div className="auto-map-viewwrap">
          <div className="auto-map-viewmeta">
            {data.rows.length} row{data.rows.length === 1 ? "" : "s"} ·{" "}
            {data.headers.length} column{data.headers.length === 1 ? "" : "s"}
          </div>
          <div className="auto-map-tablewrap">
            <table className="auto-map-table">
              <thead>
                <tr>
                  <th className="auto-map-rownum">#</th>
                  {data.headers.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.slice(0, 500).map((r, ri) => (
                  <tr key={ri}>
                    <td className="auto-map-rownum">{ri + 1}</td>
                    {data.headers.map((_, ci) => (
                      <td key={ci}>{r[ci]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.rows.length > 500 && (
            <div className="auto-map-hint">
              Showing first 500 of {data.rows.length} rows — download for the full file.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Workbook (.xlsx) map, e.g. the SPM "Agent to Profile map". Stored as-is; the
// backend validates it with the task's parser before accepting an upload, so a
// broken workbook never replaces the good one.
function WorkbookRow({ taskName, file, canEdit = false }) {
  const qc = useQueryClient();
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: info, isLoading } = useQuery({
    queryKey: ["mapFileInfo", taskName, file],
    queryFn: () => getMapFileInfo(taskName, file),
    staleTime: 60 * 1000,
  });
  const meta = info?.meta || {};
  const stats = meta.validation?.stats || {};

  const handleDownload = async () => {
    setNotice(null);
    setBusy(true);
    try {
      const res = await downloadMapFile(taskName, file);
      const bin = atob(res.content_b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = file;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setNotice({ type: "err", msg: e?.message || "Download failed" });
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (fileObj) => {
    setNotice(null);
    if (!/\.xlsx$/i.test(fileObj.name)) {
      setNotice({ type: "err", msg: "Choose an .xlsx workbook." });
      return;
    }
    setBusy(true);
    try {
      const res = await uploadMapFile(taskName, file, fileObj);
      setNotice({ type: "ok", msg: res.summary || "Uploaded." });
      qc.invalidateQueries({ queryKey: ["mapFileInfo", taskName, file] });
    } catch (e) {
      setNotice({ type: "err", msg: e?.message || "Upload failed" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auto-map">
      <div className="auto-map-head">
        <span className="auto-map-name">{file}</span>
        <div className="auto-map-actions">
          <button className="auto-eb-btn" onClick={handleDownload} disabled={busy || !info?.exists}>
            {busy ? <RefreshCw size={14} className="auto-eb-spin" /> : <Download size={14} />} Download
          </button>
          {canEdit && (
            <label className={`auto-eb-btn auto-eb-btn--primary ${busy ? "auto-eb-btn--disabled" : ""}`}>
              <Upload size={14} /> {info?.exists ? "Replace" : "Upload"} workbook
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: "none" }}
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                  e.target.value = "";
                }}
              />
            </label>
          )}
        </div>
      </div>
      <p className="auto-map-hint">
        {isLoading
          ? "Loading…"
          : !info?.exists
          ? "No workbook uploaded yet."
          : `Uploaded ${meta.uploaded_utc ? new Date(meta.uploaded_utc).toLocaleString() : "?"}` +
            (meta.uploaded_by ? ` by ${meta.uploaded_by}` : "") +
            (meta.original_name ? ` · ${meta.original_name}` : "") +
            (meta.bytes ? ` · ${Math.round(meta.bytes / 1024)} KB` : "")}
        {canEdit && " · The workbook is checked before it replaces the current one."}
      </p>
      {Object.keys(stats).length > 0 && (
        <div className="auto-map-viewmeta">
          {Object.entries(stats)
            .map(([t, s]) => `${t}: ${s.agents} agents / ${s.templates} templates${s.conflicts ? ` / ${s.conflicts} conflicts` : ""}`)
            .join(" · ")}
        </div>
      )}
      {notice && (
        <div className={`auto-eb-alert auto-eb-alert--${notice.type}`}>
          {notice.type === "ok" ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
          <span>{notice.msg}</span>
        </div>
      )}
    </div>
  );
}

export default function MappingEditor({ taskName, maps = [], canEdit = false }) {
  if (!maps.length) {
    return <div className="auto-empty">No map files for this task.</div>;
  }
  return (
    <div className="auto-map-list">
      {maps.map((f) =>
        /\.xlsx$/i.test(f) ? (
          <WorkbookRow key={f} taskName={taskName} file={f} canEdit={canEdit} />
        ) : (
          <FileRow key={f} taskName={taskName} file={f} canEdit={canEdit} />
        )
      )}
    </div>
  );
}
